/** 变更验收（G5 · changereview）。
 *
 * 规格 `docs/design/screens/changereview.md`：change-validation 卡为 planned，后端没有验收产物。
 * 列表读 `GET /changes`（定义在 changelog，W4 接）；单次详情读 `GET /changes/{change_id}/review`，
 * 现状后端如实 501「只读端点未接数据源」（裁决 B2）。落地前整屏显示「接口未提供」与返回体 detail，
 * 不渲染原型的验收记录与差额，也不放「发起验收」按钮（候选，未裁决）。
 */

import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiUrl, expectList, optionalString, query, requireString } from "../../api/client";
import { isRetryable } from "../../api/errors";
import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { ErrorState, Loading, QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { DASH } from "../../lib/format";

const READ = { retry: (count: number, error: unknown) => count < 2 && isRetryable(error), retryDelay: 1_500 } as const;

export interface ChangeRow {
  change_id: string;
  kind: string | null;
  subject: string | null;
  operator: string | null;
  ts_utc: string | null;
  verdict: string | null;
}

export function parseChanges(body: unknown): ChangeRow[] {
  const url = apiUrl("/changes");
  return expectList(body, "changes", url, "变更台账").map((row) => {
    const review = row["review"] && typeof row["review"] === "object" ? (row["review"] as Record<string, unknown>) : {};
    return {
      change_id: requireString(row, "change_id", url),
      kind: optionalString(row, "kind"),
      subject: optionalString(row, "subject"),
      operator: optionalString(row, "operator"),
      ts_utc: optionalString(row, "ts_utc"),
      verdict: optionalString(review, "verdict"),
    };
  });
}

export function ChangeReviewScreen(): ReactNode {
  const list = useQuery<ChangeRow[], Error>({ queryKey: ["changes"], queryFn: () => query<unknown>("/changes").then(parseChanges), ...READ });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <PageHead title="变更验收" sub="GET /changes · GET /changes/{change_id}/review" />
      <Lede>
        某次变更（策略 / 参数 / 数据源 / 依赖）前后各跑一次的基准跑结果、逐项差额是否都有解释、能否合入。
        change-validation 卡为 planned：验收产物落地前本屏只如实显示「接口未提供」。
      </Lede>
      <QueryState status={list.status} error={list.error} what="验收记录（change-validation「变更台账」「结果留存」）" refetch={() => void list.refetch()}>
        <Card title="验收记录" nopad>
          <Table
            cols={[{ label: "变更" }, { label: "类型" }, { label: "改了什么", wrap: true }, { label: "谁改的" }, { label: "时间" }, { label: "验收结论" }]}
            rows={(list.data ?? []).map((c) => ({
              key: c.change_id,
              selected: open === c.change_id,
              onClick: () => setOpen(open === c.change_id ? null : c.change_id),
              cells: [c.change_id, c.kind ?? DASH, c.subject ?? DASH, c.operator ?? DASH, c.ts_utc ?? DASH, <Pill key="v" tone={c.verdict === "可合入" ? "ok" : c.verdict === "禁止合入" ? "bad" : "warn"}>{c.verdict ?? "不明"}</Pill>],
            }))}
            empty="还没有任何变更验收记录"
          />
        </Card>
      </QueryState>
      {open ? <Review changeId={open} /> : null}
      <Note>发起验收入口（POST /changes/{"{change_id}"}/review）是候选，未裁决前不放按钮。</Note>
    </>
  );
}

function Review({ changeId }: { changeId: string }): ReactNode {
  const q = useQuery<unknown, Error>({
    queryKey: ["changes", changeId, "review"],
    queryFn: () => query<unknown>(`/changes/${encodeURIComponent(changeId)}/review`),
    ...READ,
  });
  if (q.status === "pending") return <Loading what={`变更 ${changeId} 的验收详情`} />;
  if (q.status === "error") return <ErrorState error={q.error} what={`变更 ${changeId} 的验收详情（change-validation「基准跑 / 差异判读」）`} onRetry={() => void q.refetch()} />;
  // 端点落地前不会走到这里；落地后按 changereview.md 字段草案补渲染，现在不猜形状。
  return <Note tone="warn">验收详情接口已返回数据，但本屏尚未按契约渲染（等 change-validation 定稿产物形状）。</Note>;
}
